import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";

// Stage 5: local replacement for @datasirpi/ui's <Select>. Ported
// letter-for-letter from the vendor's compiled bundle and
// Select-TUJIVTUC.css. Chevron switched from @datasirpi/icons' own
// ChevronDown to lucide's (identically-named, visually equivalent) —
// @datasirpi/icons is fully removed from this app now.
export type SelectSize = "sm" | "md" | "lg";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size" | "children"> {
  label: ReactNode;
  options?: ReadonlyArray<SelectOption>;
  children?: ReactNode;
  placeholder?: string;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  size?: SelectSize;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, options, children, placeholder, help, error, required, size = "md", disabled, className, id: providedId, ...rest },
  ref
) {
  const fallbackId = useId();
  const fieldId = providedId ?? fallbackId;
  const helpId = help ? `${fieldId}-help` : undefined;
  const errorId = error ? `${fieldId}-err` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cn("rc-select", `rc-select--${size}`, error && "rc-select--error", className)}>
      <label htmlFor={fieldId} className="rc-select__label">
        {label}
        {required ? (
          <span className="rc-select__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <span className="rc-select__wrap">
        <select
          ref={ref}
          id={fieldId}
          required={required}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className="rc-select__field"
          {...rest}
        >
          {placeholder !== undefined ? (
            <option value="" disabled>
              {placeholder}
            </option>
          ) : null}
          {options ? options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          )) : children}
        </select>
        <ChevronDown className="rc-select__chevron" size={16} aria-hidden />
      </span>
      {help ? (
        <span id={helpId} className="rc-select__help">
          {help}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="rc-select__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
});
